import { useQuery, useQueryClient, useMutation } from 'react-query';
import axios from 'axios';
import { API_PREFIX_URL, CACHE_KEYS } from './config';
import { api } from './swaggerApi';

const PROJECTS_CONTROLLER = 'projects';

export type NotificationStyleType = 'info' | 'warning' | 'error';
export type NotificationType = 1 | 2 | 3;
export interface CampaignCost {
  total: number;
  amount_due: number;
}
// TODO: a wave is a campaign with some included nested objects like tap_survey,
// campaign is yet to be typed
export interface Wave {
  id: number;
  identifier: string;
  name: string;
  cpi?: number | null;
  length_of_interview?: number | null;
  total_remaining?: number | null;
  status?: number | null;
  supplier_link?: string | null;
  incidence?: number | null;
  days_in_field?: number | null;
  is_retarget?: boolean | null;
  max_daily_completes?: number | null;
  reentry_interval?: number | null;
  aloi_in_sec?: number | null;
  air?: number | null;
  pre_flight_date?: string | null;
  has_unlocks_available?: boolean | null;
  tap_survey: {
    wave_number: number;
  };
  completes_count?: number;
  original_quotas_total?: number;
  completed_at?: string;
  first_complete_date?: string;
  launch_date?: string;
  is_valid?: boolean;
  campaign_cost?: CampaignCost;
}

export interface ProjectSchedule {
  id: number;
  project_id: number;
  end_type: number;
  end_date: string;
  repeat_type: number;
  repeat_cadence: number;
  repeat_on: string;
  status: 0 | 1 | 2 | 3 | 4;
  created_at: string;
  updated_at: string;
  reentry_interval: number;
  num_waves: number;

  upcoming_wave: Wave;
  current_wave: Wave;
  completed_waves: Wave[];
  next_launch?: {
    date: string;
    in_words: string;
  };
}

export interface RecurringProjectNotification {
  text: string;
  button: string | null;
  link: string;
  type: NotificationStyleType;
  notification_type: NotificationType;
  external: boolean;
}

export interface RecurringProjectCosts {
  acum_cost: number;
  estimated_next_wave_cost: number;
}
export interface NextLaunchEstimation {
  valid_next_launch: Record<string, unknown> | null;
  next_for_cadence: Record<string, unknown>;
}

export const RecurrentProjectAPI = {
  show: async (projectIdentifier: string) => {
    const url = `${API_PREFIX_URL}/${PROJECTS_CONTROLLER}/${projectIdentifier}/schedule`;
    return await axios.get<ProjectSchedule>(url);
  },
  update: async (projectIdentifier: string, params: unknown) => {
    const url = `${API_PREFIX_URL}/${PROJECTS_CONTROLLER}/${projectIdentifier}/update_schedule`;
    return await axios.put(url, params);
  },
  costs: async (projectIdentifier: string) => {
    const url = `${API_PREFIX_URL}/${PROJECTS_CONTROLLER}/${projectIdentifier}/project_costs`;
    return await axios.get<RecurringProjectCosts>(url);
  },
  estimateNextLaunch: async (projectIdentifier: string, settings: unknown) => {
    const url = `${API_PREFIX_URL}/${PROJECTS_CONTROLLER}/${projectIdentifier}/estimate_next_launch`;
    return await axios.post<NextLaunchEstimation>(url, settings);
  },
};

/*
 *  Show Project
 */
export const useRecurringProject = (projectIdentifier: string) => {
  return useQuery(
    [CACHE_KEYS.RECURRING_PROJECTS, projectIdentifier],
    async () => {
      const { data } = await RecurrentProjectAPI.show(projectIdentifier);
      return data;
    },
    {
      enabled: !!projectIdentifier,
      keepPreviousData: true,
    }
  );
};

/*
 *  Update Project Schedule
 */
export const useUpdateProjectSchedule = (projectIdentifier: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (params) => {
      const { data } = await RecurrentProjectAPI.update(projectIdentifier, {
        project_schedule: params,
      });
      return data;
    },
    {
      onSuccess: (res) => {
        queryClient.setQueryData(
          [CACHE_KEYS.RECURRING_PROJECTS, projectIdentifier],
          res
        );
      },
    }
  );
};

export const useGetRecurrentProjectCosts = (projectIdentifier: string) =>
  useQuery(
    [CACHE_KEYS.PROJECT_COSTS, projectIdentifier],
    async () => {
      const { data } = await RecurrentProjectAPI.costs(projectIdentifier);
      return data;
    },
    { keepPreviousData: true }
  );

export const useEstimateNextLaunch = ({
  projectIdentifier,
  settings,
}: {
  projectIdentifier?: string;
  settings?: Record<string, unknown>;
}) => {
  const { end_date, end_type, repeat_type, repeat_cadence, repeat_on } =
    settings || {};
  const queryKeys = {
    end_date,
    end_type,
    repeat_type,
    repeat_cadence,
    repeat_on,
  };
  return useQuery(
    [CACHE_KEYS.NEXT_LAUNCH_ESTIMATES, { ...queryKeys }],
    async () => {
      const { data } = await RecurrentProjectAPI.estimateNextLaunch(
        projectIdentifier,
        { project_schedule: settings }
      );
      const { valid_next_launch, next_for_cadence } = data || {};
      return {
        nextLaunchValid: !!valid_next_launch,
        nextLaunchEstimated: valid_next_launch,
        nextLaunchForCadence: next_for_cadence,
      };
    },
    {
      keepPreviousData: true,
      enabled: !!projectIdentifier && !!settings,
      // Given the same schedule settings (defined by queryKeys) -> cache the validation result for 5 minutes
      staleTime: 1000 * 60 * 5,
    }
  );
};

export const useProjectWaves = (projectIdentifier: string, limit?: number) => {
  return useQuery(
    [CACHE_KEYS.PROJECT_WAVES, projectIdentifier],
    async () => {
      const { data } = await api.insightsPlatform.projectsWavesDetail({
        identifier: projectIdentifier,
        ...(limit && {
          limit,
        }),
      });
      return data;
    },
    {
      enabled: !!projectIdentifier,
    }
  );
};
